import { useRef, useState } from "react";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Upload, X, Star, GripVertical, ImagePlus, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";

// Read an AI-enhanceable field (stored {submitted,enhanced,...}) back to its editable text.
const enhVal = (f) => (f && typeof f === "object" ? f.submitted || "" : f || "");

const normGallery = (g) =>
  (g || []).map((it, i) =>
    typeof it === "string"
      ? { file_id: it, caption: "", order: i }
      : { file_id: it.file_id || it.url || "", caption: it.caption || "", order: it.order ?? i }
  ).sort((a, b) => a.order - b.order);

export default function BikePathContentTab({ pathId, content, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const c = content || {};
  const [hero, setHero] = useState(c.hero_image || null);
  const [cover, setCover] = useState(c.cover_image || null);
  const [gallery, setGallery] = useState(normGallery(c.gallery));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const dragIdx = useRef(null);
  const heroRef = useRef();
  const galRef = useRef();

  const [f, setF] = useState({
    description: enhVal(c.description),
    length_mi: c.length_mi ?? "",
    surface_type: c.surface_type || "",
    surface_paved_pct: c.surface_paved_pct ?? "",
    surface_crushed_stone_pct: c.surface_crushed_stone_pct ?? "",
    surface_gravel_pct: c.surface_gravel_pct ?? "",
    surface_dirt_pct: c.surface_dirt_pct ?? "",
    difficulty: c.difficulty || "",
    elevation_gain_ft: c.elevation_gain_ft ?? "",
    estimated_ride_time: c.estimated_ride_time || "",
    towns_served: (c.towns_served || []).join(", "),
    family_friendly: !!c.family_friendly,
    beginner_friendly: !!c.beginner_friendly,
    ebike_friendly: !!c.ebike_friendly,
    dog_friendly: !!c.dog_friendly,
    ada_notes: enhVal(c.ada_notes),
    scenic_highlights: enhVal(c.scenic_highlights),
    historical_info: enhVal(c.historical_info),
    seasonal_info: enhVal(c.seasonal_info),
    safety_notes: enhVal(c.safety_notes),
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // --- AI content generation ---
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const generateAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/admin/bike-paths/${pathId}/ai/content`);
      setAiResult(data.generated);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "AI generation failed"); }
    finally { setAiLoading(false); }
  };
  const applyAi = (mode) => {
    const g = aiResult;
    if (!g) return;
    const incoming = { ...g, towns_served: Array.isArray(g.towns_served) ? g.towns_served.join(", ") : (g.towns_served || "") };
    setF((s) => {
      const next = { ...s };
      Object.keys(incoming).forEach((k) => {
        if (!(k in s)) return;
        const val = incoming[k];
        if (val === null || val === undefined) return;
        if (typeof s[k] === "boolean") { if (mode === "all") next[k] = !!val; return; }
        const isEmpty = s[k] === "" || s[k] === null || s[k] === undefined;
        if (mode === "all" || (mode === "empty" && isEmpty)) next[k] = val;
      });
      return next;
    });
    setAiResult(null);
    toast.success(mode === "all" ? "AI content applied — review and save" : "Filled empty fields — review and save");
  };

  const uploadOne = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
    return data.file_id;
  };

  const handleHero = async (files) => {
    setUploading(true);
    try { setHero(await uploadOne(files[0])); }
    catch { toast.error("Upload failed. Images up to 8MB."); }
    finally { setUploading(false); }
  };

  const handleGallery = async (files) => {
    setUploading(true);
    try {
      const ids = [];
      for (const file of Array.from(files)) ids.push(await uploadOne(file));
      setGallery((g) => [...g, ...ids.map((id, i) => ({ file_id: id, caption: "", order: g.length + i }))]);
    } catch { toast.error("Upload failed. Images up to 8MB."); }
    finally { setUploading(false); }
  };

  const removeGal = (fid) => {
    setGallery((g) => g.filter((x) => x.file_id !== fid).map((x, i) => ({ ...x, order: i })));
    if (cover === fid) setCover(null);
  };
  const setCaption = (fid, cap) => setGallery((g) => g.map((x) => (x.file_id === fid ? { ...x, caption: cap } : x)));

  const onDrop = (toIdx) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from === null || from === toIdx) return;
    setGallery((g) => {
      const arr = [...g];
      const [moved] = arr.splice(from, 1);
      arr.splice(toIdx, 0, moved);
      return arr.map((x, i) => ({ ...x, order: i }));
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const num = (v) => (v === "" || v === null ? null : Number(v));
      const payload = {
        description: f.description,
        hero_image: hero,
        cover_image: cover,
        gallery: gallery.map((x, i) => ({ file_id: x.file_id, caption: x.caption, order: i })),
        length_mi: num(f.length_mi),
        surface_type: f.surface_type,
        surface_paved_pct: num(f.surface_paved_pct),
        surface_crushed_stone_pct: num(f.surface_crushed_stone_pct),
        surface_gravel_pct: num(f.surface_gravel_pct),
        surface_dirt_pct: num(f.surface_dirt_pct),
        difficulty: f.difficulty,
        elevation_gain_ft: num(f.elevation_gain_ft),
        estimated_ride_time: f.estimated_ride_time,
        towns_served: f.towns_served.split(",").map((t) => t.trim()).filter(Boolean),
        family_friendly: f.family_friendly,
        beginner_friendly: f.beginner_friendly,
        ebike_friendly: f.ebike_friendly,
        dog_friendly: f.dog_friendly,
        ada_notes: f.ada_notes,
        scenic_highlights: f.scenic_highlights,
        historical_info: f.historical_info,
        seasonal_info: f.seasonal_info,
        safety_notes: f.safety_notes,
      };
      const { data } = await api.put(`/admin/bike-paths/${pathId}/content`, payload);
      toast.success("Content & media saved");
      onSaved?.(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const Section = ({ title, children }) => (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4">
      <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </div>
  );
  const Rich = ({ k, label, placeholder, rows = 3 }) => (
    <div>
      <Label>{label}</Label>
      <Textarea rows={rows} value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} data-testid={`bpc-${k}-input`} />
    </div>
  );
  const Bool = ({ k, label }) => (
    <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
      <span className="text-sm">{label}</span>
      <Switch checked={f[k]} onCheckedChange={(v) => set(k, v)} data-testid={`bpc-${k}-switch`} />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="bikepath-content-tab">
      {/* Media */}
      <Section title="Media">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>Hero Image <span className="text-muted-foreground font-normal">(top of detail page)</span></Label>
            <div className="mt-2 flex items-center gap-3">
              {hero ? (
                <div className="relative w-40 h-28 rounded-lg overflow-hidden border border-border group">
                  <img src={fileUrl(hero)} alt="hero" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setHero(null)} data-testid="bpc-hero-remove" className="absolute top-1 right-1 bg-black/70 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3 text-white" /></button>
                </div>
              ) : (
                <button type="button" onClick={() => heroRef.current?.click()} data-testid="bpc-hero-add" className="w-40 h-28 rounded-lg border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-primary transition-colors">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}<span className="text-xs">Hero</span>
                </button>
              )}
              <input ref={heroRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.length && handleHero(e.target.files)} />
            </div>
          </div>
          <div>
            <Label>Cover Image <span className="text-muted-foreground font-normal">(cards & home banner)</span></Label>
            <div className="mt-2 flex items-center gap-3">
              {cover ? (
                <div className="relative w-40 h-28 rounded-lg overflow-hidden border-2 border-amber-400 group">
                  <img src={fileUrl(cover)} alt="cover" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setCover(null)} data-testid="bpc-cover-remove" className="absolute top-1 right-1 bg-black/70 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3 text-white" /></button>
                </div>
              ) : (
                <div className="w-40 h-28 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-xs text-muted-foreground text-center px-2" data-testid="bpc-cover-empty">
                  Pick a gallery image below, or falls back to Hero
                </div>
              )}
            </div>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label>Gallery <span className="text-muted-foreground font-normal">(drag to reorder · set a cover · add captions)</span></Label>
            <Button type="button" size="sm" variant="outline" onClick={() => galRef.current?.click()} data-testid="bpc-gallery-add">
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Add images
            </Button>
            <input ref={galRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files?.length && handleGallery(e.target.files)} />
          </div>
          {gallery.length === 0 ? (
            <div className="mt-2 text-sm text-muted-foreground rounded-lg border border-dashed border-border p-6 text-center" data-testid="bpc-gallery-empty">No gallery images yet.</div>
          ) : (
            <div className="mt-3 space-y-2" data-testid="bpc-gallery-list">
              {gallery.map((g, i) => (
                <div key={g.file_id} draggable onDragStart={() => (dragIdx.current = i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                  className={`flex items-center gap-3 rounded-lg border p-2 bg-card ${cover === g.file_id ? "border-amber-400" : "border-border"}`} data-testid={`bpc-gallery-item-${i}`}>
                  <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab shrink-0" />
                  <img src={fileUrl(g.file_id)} alt="" className="w-16 h-16 rounded object-cover shrink-0" />
                  <Input value={g.caption} onChange={(e) => setCaption(g.file_id, e.target.value)} placeholder="Optional caption" className="flex-1" data-testid={`bpc-gallery-caption-${i}`} />
                  <Button type="button" size="sm" variant={cover === g.file_id ? "default" : "outline"} onClick={() => setCover(g.file_id)} data-testid={`bpc-gallery-cover-${i}`}>
                    <Star className={`w-4 h-4 mr-1 ${cover === g.file_id ? "fill-current" : ""}`} />{cover === g.file_id ? "Cover" : "Set cover"}
                  </Button>
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeGal(g.file_id)} data-testid={`bpc-gallery-remove-${i}`}><X className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* Overview */}
      <Section title="Overview">
        <Rich k="description" label="Description" placeholder="A scenic paved rail-trail along Narragansett Bay…" rows={4} />
      </Section>

      {/* Ride & trail details */}
      <Section title="Ride & Trail Details">
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label>Length (mi)</Label><Input type="number" value={f.length_mi} onChange={(e) => set("length_mi", e.target.value)} data-testid="bpc-length_mi-input" /></div>
          <div><Label>Elevation Gain (ft)</Label><Input type="number" value={f.elevation_gain_ft} onChange={(e) => set("elevation_gain_ft", e.target.value)} data-testid="bpc-elevation_gain_ft-input" /></div>
          <div><Label>Estimated Ride Time</Label><Input value={f.estimated_ride_time} onChange={(e) => set("estimated_ride_time", e.target.value)} placeholder="≈ 1h 45m" data-testid="bpc-estimated_ride_time-input" /></div>
          <div><Label>Surface Type</Label><Input value={f.surface_type} onChange={(e) => set("surface_type", e.target.value)} placeholder="Paved asphalt" data-testid="bpc-surface_type-input" /></div>
          <div><Label>Difficulty</Label><Input value={f.difficulty} onChange={(e) => set("difficulty", e.target.value)} placeholder="Easy / Moderate / Hard" data-testid="bpc-difficulty-input" /></div>
          <div className="sm:col-span-3"><Label>Towns Served <span className="text-muted-foreground font-normal">(comma-separated)</span></Label><Input value={f.towns_served} onChange={(e) => set("towns_served", e.target.value)} placeholder="Providence, East Providence, Barrington, Bristol" data-testid="bpc-towns_served-input" /></div>
        </div>
        <div>
          <Label>Surface Breakdown (%)</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1">
            <div><span className="text-xs text-muted-foreground">Paved</span><Input type="number" value={f.surface_paved_pct} onChange={(e) => set("surface_paved_pct", e.target.value)} data-testid="bpc-surface_paved_pct-input" /></div>
            <div><span className="text-xs text-muted-foreground">Crushed Stone</span><Input type="number" value={f.surface_crushed_stone_pct} onChange={(e) => set("surface_crushed_stone_pct", e.target.value)} data-testid="bpc-surface_crushed_stone_pct-input" /></div>
            <div><span className="text-xs text-muted-foreground">Gravel</span><Input type="number" value={f.surface_gravel_pct} onChange={(e) => set("surface_gravel_pct", e.target.value)} data-testid="bpc-surface_gravel_pct-input" /></div>
            <div><span className="text-xs text-muted-foreground">Dirt</span><Input type="number" value={f.surface_dirt_pct} onChange={(e) => set("surface_dirt_pct", e.target.value)} data-testid="bpc-surface_dirt_pct-input" /></div>
          </div>
        </div>
      </Section>

      {/* Accessibility */}
      <Section title="Accessibility & Suitability">
        <div className="grid sm:grid-cols-2 gap-3">
          <Bool k="family_friendly" label="Family Friendly" />
          <Bool k="beginner_friendly" label="Beginner Friendly" />
          <Bool k="ebike_friendly" label="E-Bike Friendly" />
          <Bool k="dog_friendly" label="Dog Friendly" />
        </div>
        <Rich k="ada_notes" label="ADA / Accessibility Notes" placeholder="Wheelchair-accessible restrooms at trailheads; gentle grades throughout…" />
      </Section>

      {/* Highlights */}
      <Section title="Highlights & Storytelling">
        <Rich k="scenic_highlights" label="Scenic Highlights" placeholder="Sweeping bay views, salt marshes, historic Bristol harbor…" />
        <Rich k="historical_info" label="Historical Information" placeholder="Built on the former Providence–Bristol railroad corridor…" />
        <Rich k="seasonal_info" label="Seasonal Information" placeholder="Best in fall foliage; sections icy in winter…" />
        <Rich k="safety_notes" label="Safety Notes" placeholder="Several road crossings; yield at Veterans Memorial Pkwy…" />
      </Section>

      <div className="flex justify-end items-center gap-3 sticky bottom-0 py-3 bg-background/80 backdrop-blur">
        {isAdmin && (
          <Button type="button" variant="outline" onClick={generateAi} disabled={aiLoading || saving} data-testid="bpc-generate-ai"
            className="border-primary/40 text-primary hover:bg-primary/10">
            {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Generate AI Content
          </Button>
        )}
        <Button onClick={save} disabled={saving || uploading} data-testid="bpc-save">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save content & media</Button>
      </div>

      <Dialog open={!!aiResult} onOpenChange={(o) => { if (!o) setAiResult(null); }}>
        <DialogContent data-testid="bpc-ai-modal">
          <DialogHeader>
            <DialogTitle>Replace existing content?</DialogTitle>
            <DialogDescription>
              AI generated new content for this bike path. Choose how to apply it — your changes aren't saved until you click “Save content & media”.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setAiResult(null)} data-testid="bpc-ai-cancel">Cancel</Button>
            <Button variant="outline" onClick={() => applyAi("empty")} data-testid="bpc-ai-fill-empty">Fill Empty Fields Only</Button>
            <Button onClick={() => applyAi("all")} data-testid="bpc-ai-replace-all">Replace All</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
